// AssetBar - 下部のアセット選択バー
// キャラ・ゴール + スロット + 一覧ボタン

import { useDrag } from "react-dnd";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useRef, useMemo, useState, useEffect, PointerEvent } from "react";
import AppsIcon from "@mui/icons-material/Apps";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { useScale, usePlaySound } from "common/utils";
import { AssetModel, AssetModelType } from "common/model";
import { actions, selectAllMapModel } from "features/builder/slice";
import { AssetListModal } from "./AssetListModal";
import { EditHistoryButton } from "./EditHistoryButton";
import { BuilderGuideTargetId } from "./BuilderTutorialOverlay";

// アセット枠の基準サイズ（useScale で画面サイズに応じて拡縮）
const ITEM_BASE = 80;

// アセットアイテムコンポーネント
const AssetItem = ({
  asset,
  selected,
  disabled,
  onSelect,
  onBlocked,
}: {
  asset: AssetModel;
  selected: boolean;
  disabled?: boolean;
  onSelect: (asset: AssetModel) => void;
  /** 押せないときに、その場所で理由を伝えるため（黙って無反応にしない） */
  onBlocked?: (rect: DOMRect) => void;
}) => {
  const [isPressed, setIsPressed] = useState(false);

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: asset.type,
    item: asset,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  useEffect(() => {
    if (isDragging) {
      setIsPressed(false);
      if (onSelect && !disabled) {
        onSelect(asset);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  const handlePointerDown = () => setIsPressed(true);
  const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isPressed && disabled) {
      onBlocked?.(e.currentTarget.getBoundingClientRect());
      setIsPressed(false);
      return;
    }
    if (isPressed && onSelect && !disabled) {
      onSelect(asset);
      setIsPressed(false);
    }
  };

  return (
    <div
      ref={(node) => {
        if (onSelect && !disabled && drag) drag(node);
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      className={`flex-col-el flex-center w-full h-full rounded-lg overflow-hidden cursor-pointer transition-all duration-150 active:scale-95
        ${selected ? "ring-2 ring-blue-80 bg-blue-10" : ""}
        ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-white/50"}
      `}
    >
      <img
        draggable="false"
        className="w-full h-full object-contain select-none p-1"
        src={`${Constants.assetHost}/assets/models/${asset.name}.png`}
      />
    </div>
  );
};

// 空のスロット
const EmptySlot = ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
  <div
    onClick={onClick}
    className={`flex-col-el flex-center w-full h-full rounded-lg cursor-pointer transition-all duration-150 active:scale-95 border-2 border-dashed
      ${selected ? "border-blue-80 bg-blue-10" : "border-gray-30 hover:border-gray-40 hover:bg-white/30"}
    `}
  >
    <span className="text-2xl text-gray-40">+</span>
  </div>
);

export const AssetBar = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const allMapModel = useSelector(selectAllMapModel);
  const selectedSlotIndex = useSelector((state: RootState) => state.builder.selectedSlotIndex);
  const selectedAssetModel = useSelector((state: RootState) => state.builder.selectedAssetModel);
  const showAssetList = useSelector((state: RootState) => state.builder.actionMenu.assetList);
  const slot = useSelector((state: RootState) => state.builder.slot);

  // 固定アセット
  const character = useMemo<AssetModel>(
    () => ({
      type: AssetModelType.CHARACTER,
      name: "character_duck",
      filename: "character_duck.glb",
      rotation: true,
      offset: { x: 0, y: -0.4, z: 0 },
      scaling: { x: 1.2, y: 1.2, z: 1.2 },
    }),
    [],
  );

  const goal = useMemo<AssetModel>(
    () => ({
      type: AssetModelType.GOAL,
      name: "flag_teamRed",
      filename: "flag_teamRed.gltf.glb",
    }),
    [],
  );

  const hasCharacter = allMapModel.some((m) => m.type === AssetModelType.CHARACTER);
  // ゴールもキャラクターと同じく1つだけ。2つ置けてしまっていた
  const hasGoal = allMapModel.some((m) => m.type === AssetModelType.GOAL);

  // 押せないブロックを押したときに、その場で理由を伝える
  const handleBlocked = (messageKey: string) => (rect: DOMRect) => {
    play();
    dispatch(actions.showPlaceHint({ messageKey, x: rect.left + rect.width / 2, y: rect.top }));
  };

  const handleSelectAsset = (slotIndex?: number) => (asset: AssetModel) => {
    play();
    dispatch(actions.updateActionMenu({ key: "asset", value: true, exclusive: true }));
    dispatch(actions.selectedAssetMesh(asset));
    dispatch(actions.selectSlot(slotIndex ?? null));
  };

  const handleSelectSlot = (index: number) => () => {
    dispatch(actions.selectSlot(index));
    if (!slot[index]) {
      play();
      dispatch(actions.updateActionMenu({ key: "assetList", value: true, exclusive: true }));
      dispatch(actions.selectedAssetMesh(null));
    }
  };

  const handleOpenAssetList = () => {
    play();
    // 空のスロットを探す
    let targetSlot = selectedSlotIndex;
    if (targetSlot === null || targetSlot === undefined) {
      targetSlot = slot.findIndex((s) => !s);
      if (targetSlot === -1) targetSlot = 0;
    }
    dispatch(actions.selectSlot(targetSlot));
    dispatch(
      actions.updateActionMenu({ key: "assetList", value: !showAssetList, exclusive: true }),
    );
  };

  return createPortal(
    <div
      ref={containerRef}
      className="flex-col-el absolute left-0 right-0 z-[10000] pointer-events-none"
      style={{ bottom: 8 * scale }}
    >
      <AssetListModal />

      <div className="flex-row-el justify-center w-full">
        <div
          style={{ transform: `translateX(${40 * scale}px) scale(${scale})` }}
          className="flex-row-el items-end gap-3 origin-bottom pointer-events-auto"
        >
          <div
            data-guide="editor_assets"
            className="flex-row-el items-center gap-3 px-4 py-2 rounded-2xl bg-white/80 backdrop-blur-sm border-2 border-white shadow-lg"
          >
            {/* 必須アセット: キャラ・ゴール */}
            <div className="flex-row-el items-center gap-2 pr-3 border-r-2 border-gray-20">
              <div
                id={BuilderGuideTargetId.ASSET_CHARACTER}
                style={{ width: ITEM_BASE, height: ITEM_BASE }}
                className="shrink-0"
              >
                <AssetItem
                  asset={character}
                  selected={selectedAssetModel?.name === character.name}
                  disabled={hasCharacter}
                  onSelect={handleSelectAsset()}
                  onBlocked={handleBlocked("MSG_BUILDER_ONLY_ONE_CHARACTER")}
                />
              </div>
              <div
                id={BuilderGuideTargetId.ASSET_GOAL}
                style={{ width: ITEM_BASE, height: ITEM_BASE }}
                className="shrink-0"
              >
                <AssetItem
                  asset={goal}
                  selected={selectedAssetModel?.name === goal.name}
                  disabled={hasGoal}
                  onSelect={handleSelectAsset()}
                  onBlocked={handleBlocked("MSG_BUILDER_ONLY_ONE_GOAL")}
                />
              </div>
            </div>

            {/* カスタムスロット */}
            <div id={BuilderGuideTargetId.ASSET_SLOTS} className="flex-row-el items-center gap-2">
              {slot.map((slotAsset, index) => (
                <div
                  key={`${slotAsset?.name}_${index}`}
                  style={{ width: ITEM_BASE, height: ITEM_BASE }}
                  className="shrink-0"
                >
                  {slotAsset ? (
                    <AssetItem
                      asset={slotAsset}
                      selected={
                        selectedSlotIndex === index || selectedAssetModel?.name === slotAsset.name
                      }
                      onSelect={handleSelectAsset(index)}
                    />
                  ) : (
                    <EmptySlot
                      selected={selectedSlotIndex === index}
                      onClick={handleSelectSlot(index)}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* 一覧ボタン。押せると分かるよう、りれきボタンと同じ白カード＋影にする */}
            <div
              data-guide="asset_open"
              className="flex-row-el items-center pl-3 border-l-2 border-gray2-30"
            >
              <button
                type="button"
                onClick={handleOpenAssetList}
                aria-label={I18n.t("MSG_BUILDER_ASSET_LIST_TITLE")}
                style={{ width: ITEM_BASE, height: ITEM_BASE }}
                className={`type-inherit-font flex-col-el flex-center gap-1 box-border rounded-2xl shadow-lg border-solid border-2 transition-all duration-150 active:scale-95 cursor-pointer ${
                  showAssetList ? "bg-blue-10 border-blue-100" : "bg-white border-white"
                }`}
              >
                <span className="flex-col-el flex-center type-title text-blue-100">
                  <AppsIcon fontSize="inherit" />
                </span>
                <span className="type-sm text-textcolor-black whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_ASSET_LIST_SHORT")}
                </span>
              </button>
            </div>
          </div>
          <EditHistoryButton />
        </div>
      </div>
    </div>,
    document.body,
  );
};
